// エクスポートボタンコンポーネント

import { useState } from 'react';
import { CalculationResult, CalculationInput, CalculationHistory } from '../types';
import { generateCsv } from '../utils/api';
import { downloadBlob, generateFilename } from '../utils/storage';

interface ExportButtonsProps {
  result: CalculationResult;
  svg: string;
  inputData: CalculationInput;
  history: CalculationHistory[];
}

export function ExportButtons({
  result,
  svg,
  inputData,
  history,
}: ExportButtonsProps) {
  const [isExporting, setIsExporting] = useState(false);

  const handleCsvExport = async (exportType: 'current' | 'all') => {
    setIsExporting(true);
    
    try {
      let data;
      
      if (exportType === 'current') {
        // 現在の結果のみ
        data = [{
          result,
          pieceWidth: inputData.pieceWidth,
          pieceHeight: inputData.pieceHeight,
          kerf: inputData.kerf,
        }];
      } else {
        // 全履歴
        data = history.map(item => ({
          result: item.result,
          pieceWidth: item.input.pieceWidth,
          pieceHeight: item.input.pieceHeight,
          kerf: item.input.kerf,
        }));
      }

      const blob = await generateCsv(data);
      const filename = exportType === 'current' 
        ? generateFilename(`cutting_result_${result.materialName}`) + '.csv'
        : generateFilename('cutting_results_all') + '.csv';
      
      downloadBlob(blob, filename);
    } catch (error) {
      console.error('CSV export error:', error);
      alert('CSVファイルの生成に失敗しました。');
    } finally {
      setIsExporting(false);
    }
  };

  const handleSvgExport = () => {
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const filename = generateFilename(`cutting_diagram_${result.materialName}`) + '.svg';
    downloadBlob(blob, filename);
  };

  // Add HTML escaping utility
  const escapeHtml = (text: string) => {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  };

  const handlePrintDiagram = () => {
    // 印刷用のウィンドウを開く
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>切断配置図 - ${escapeHtml(result.materialName)}</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: Arial, sans-serif;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .diagram {
              text-align: center;
              page-break-inside: avoid;
            }
            .info {
              margin-top: 20px;
              font-size: 14px;
            }
            @media print {
              .no-print {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>切断配置図</h1>
            <p>部材名: ${escapeHtml(result.materialName)} | 取れる枚数: ${result.totalPieces}枚 | 配置方法: ${escapeHtml(result.orientation)}</p>
          </div>
          <div class="diagram">
            ${svg}
          </div>
          <div class="info">
            <p>選択板材: ${result.selectedVeneer} (${result.veneerWidth} × ${result.veneerHeight} mm)</p>
            <p>水平方向: ${result.horizontalCount}枚 | 垂直方向: ${result.verticalCount}枚</p>
            <p>作成日時: ${new Date().toLocaleString('ja-JP')}</p>
          </div>
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  return (
    <div className="card">
      <h2 className="text-xl font-semibold text-gray-900 mb-6">
        エクスポート・共有
      </h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* CSV エクスポート (現在の結果) */}
        <button
          onClick={() => handleCsvExport('current')}
          disabled={isExporting}
          className="btn-secondary flex-1"
          title="現在の計算結果をCSVファイルでダウンロード"
        >
          <div className="flex flex-col items-center">
            <svg className="h-5 w-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-xs leading-tight text-center">
              結果を<br />CSV保存
            </span>
          </div>
        </button>

        {/* CSV エクスポート (全履歴) */}
        <button
          onClick={() => handleCsvExport('all')}
          disabled={isExporting || history.length === 0}
          className="btn-secondary flex-1"
          title="計算履歴全体をCSVファイルでダウンロード"
        >
          <div className="flex flex-col items-center">
            <svg className="h-5 w-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
            </svg>
            <span className="text-xs leading-tight text-center">
              履歴を<br />CSV保存
            </span>
          </div>
        </button>

        {/* SVG エクスポート */}
        <button
          onClick={handleSvgExport}
          className="btn-secondary flex-1"
          title="切断配置図をSVGファイルでダウンロード"
        >
          <div className="flex flex-col items-center">
            <svg className="h-5 w-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-xs leading-tight text-center">
              図面を<br />SVG保存
            </span>
          </div>
        </button>

        {/* 印刷 */}
        <button
          onClick={handlePrintDiagram}
          className="btn-secondary flex-1"
          title="切断配置図を印刷"
        >
          <div className="flex flex-col items-center">
            <svg className="h-5 w-5 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span className="text-xs leading-tight text-center">
              図面を<br />印刷
            </span>
          </div>
        </button>
      </div>

      {isExporting && (
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="animate-spin h-5 w-5 text-blue-600 mr-3" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <span className="text-blue-700">ファイルを生成中...</span>
          </div>
        </div>
      )}
    </div>
  );
}